import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { EventType, InteractionStatus, InteractionType } from '@azure/msal-browser';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';

import AuthService, { isPopupBlockedError, isPopupClosedError } from 'services/auth.service';
import { SET_USER } from 'store/actions';
import { initialState } from 'store/sessionReducer';
import { loginRequest } from '../../../authConfig';

export default function Login() {
    const { t, i18n } = useTranslation('login');
    const { instance, accounts, inProgress } = useMsal();

    const form = useRef();
    const dispatch = useDispatch();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [useLegacyAuth, setUseLegacyAuth] = useState(false);
    // Account picker menu anchor
    const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
    const accountMenuOpen = Boolean(accountMenuAnchor);

    const message = useSelector((state) => state.session.errorMessage);

    /**
     * Completes the backend login when MSAL returns from a redirect sign in
     * (used when the popup was blocked by the browser).
     */
    useEffect(() => {
        const callbackId = instance.addEventCallback((event) => {
            if (event.interactionType !== InteractionType.Redirect) {
                return;
            }
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
                setLoading(true);
                AuthService.loginWithMsal(event.payload.account, event.payload.accessToken).finally(() => setLoading(false));
            } else if (event.eventType === EventType.LOGIN_FAILURE) {
                dispatch({
                    type: SET_USER,
                    user: undefined,
                    username: '',
                    errorLogin: true,
                    errorMessage: event.error?.message || t('authFailed')
                });
            }
        });
        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance, dispatch, t]);

    /**
     * Acquires a token silently for a known account and calls the backend.
     */
    const handleMsalLoginWithAccount = async (account) => {
        try {
            setLoading(true);
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account
            });
            await AuthService.loginWithMsal(account, response.accessToken);
        } catch (error) {
            console.error('Silent token acquisition failed:', error);
            if (error.name === 'InteractionRequiredAuthError') {
                // Force interactive login for this specific account
                await _loginPopup({ loginHint: account.username });
            }
        } finally {
            setLoading(false);
        }
    };

    /**
     * Internal helper that opens the B2C popup and calls the backend.
     * @param {object} extraParams - Extra params forwarded to loginPopup (e.g. loginHint, prompt)
     */
    const _loginPopup = async (extraParams = {}) => {
        try {
            setLoading(true);
            const response = await instance.loginPopup({ ...loginRequest, ...extraParams });
            if (response && response.account) {
                await AuthService.loginWithMsal(response.account, response.accessToken);
            }
        } catch (error) {
            console.error('MSAL login failed:', error);
            if (isPopupBlockedError(error)) {
                // Browser blocked the popup: continue the sign in with a full page redirect.
                // The result is handled by the LOGIN_SUCCESS event callback below.
                await instance.loginRedirect({ ...loginRequest, ...extraParams });
                return;
            }
            dispatch({
                type: SET_USER,
                user: undefined,
                username: '',
                errorLogin: true,
                errorMessage: isPopupClosedError(error) ? t('popupClosed') : error.message || t('authFailed')
            });
        } finally {
            setLoading(false);
        }
    };

    /**
     * Called when the user clicks the primary Sign In button.
     * - 0 cached accounts  → open B2C popup directly
     * - 1+ cached accounts → show account picker so the user can use the cached
     *                        account, sign in with a different one, or add a new one
     */
    const handleMsalSignIn = (event) => {
        if (accounts.length >= 1) {
            setAccountMenuAnchor(event.currentTarget);
        } else {
            _loginPopup();
        }
    };

    const handleAccountMenuClose = () => setAccountMenuAnchor(null);

    const handlePickAccount = async (account) => {
        handleAccountMenuClose();
        await handleMsalLoginWithAccount(account);
    };

    const handleSignInWithDifferentAccount = async () => {
        handleAccountMenuClose();
        // prompt: 'login' forces B2C to show the login screen regardless of existing sessions
        await _loginPopup({ prompt: 'login' });
    };

    const onChangeUsername = (e) => {
        const username = e.target.value;
        setUsername(username);
    };

    const onChangePassword = (e) => {
        const password = e.target.value;
        setPassword(password);
    };

    const handleLegacyLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        await AuthService.login(username, password);
        setLoading(false);
    };

    return (
        <div>
            <div id="logo-login"></div>
            <div id="main" className="container">
                <div className="row">
                    <div className="col-lg-4">
                        {!useLegacyAuth ? (
                            <div className="form-horizontal form-login">
                                <div className="form-group">
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-block"
                                        onClick={handleMsalSignIn}
                                        disabled={loading || inProgress !== InteractionStatus.None}
                                    >
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span>{t('signIn')}</span>
                                    </button>

                                    {/* Account picker — shown when 2+ accounts are cached */}
                                    <Menu
                                        anchorEl={accountMenuAnchor}
                                        open={accountMenuOpen}
                                        onClose={handleAccountMenuClose}
                                        PaperProps={{ sx: { minWidth: 260 } }}
                                    >
                                        {accounts.map((account) => (
                                            <MenuItem
                                                key={account.homeAccountId}
                                                onClick={() => handlePickAccount(account)}
                                            >
                                                <ListItemIcon>
                                                    <PersonIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={account.name || account.username}
                                                    secondary={account.username !== account.name ? account.username : undefined}
                                                />
                                            </MenuItem>
                                        ))}
                                        <Divider />
                                        <MenuItem onClick={handleSignInWithDifferentAccount}>
                                            <ListItemIcon>
                                                <AddIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary={t('signInWithDifferentAccount') || 'Use a different account'} />
                                        </MenuItem>
                                    </Menu>
                                </div>
                                {/*<div className="form-group text-center">*/}
                                {/*    <button*/}
                                {/*        type="button"*/}
                                {/*        className="btn btn-link"*/}
                                {/*        onClick={() => setUseLegacyAuth(true)}*/}
                                {/*    >*/}
                                {/*        Use legacy authentication*/}
                                {/*    </button>*/}
                                {/*</div>*/}
                                {message && (
                                    <div className="alert alert-danger" role="alert">
                                        {message}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <form
                                className="form-horizontal form-login"
                                encType="multipart/form-data"
                                id="frm"
                                onSubmit={handleLegacyLogin}
                                ref={form}
                            >
                                <div className="input-group m-b-20">
                                    <div className="fg-line">
                                        <TextField
                                            label="Usuario"
                                            type="search"
                                            value={username}
                                            onChange={onChangeUsername}
                                            variant="standard"
                                            placeholder="Usuario"
                                            fullWidth
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="input-group m-b-20">
                                    <div className="fg-line">
                                        <TextField
                                            label="Contraseña"
                                            type="password"
                                            value={password}
                                            onChange={onChangePassword}
                                            variant="standard"
                                            placeholder="Contraseña"
                                            fullWidth
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span>{t('signIn')}</span>
                                    </button>
                                </div>
                                <div className="form-group text-center">
                                    <button
                                        type="button"
                                        className="btn btn-link"
                                        onClick={() => setUseLegacyAuth(false)}
                                    >
                                        Back to Microsoft Sign In
                                    </button>
                                </div>
                                {message && (
                                    <div className="alert alert-danger" role="alert">
                                        {message}
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                    <div className="col-lg-8">
                        <div className="cont-det b-y">
                            <h3>{t('conecta')}</h3>
                            <p>{t('conectaDescription')}</p>
                        </div>
                        <div className="cont-det b-p">
                            <h3>{t('distribuye')}</h3>
                            <p>{t('distribuyeDescription')}</p>
                        </div>
                        <div className="cont-det b-g">
                            <h3>{t('personaliza')}</h3>
                            <p>{t('personalizaDescription')}</p>
                        </div>
                        <div className="cont-det b-v">
                            <h3>{t('facilita')}</h3>
                            <p>{t('facilitaDescription')}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
